import React, { useEffect } from 'react';

import { SIGN_UP_STATES } from '@jetslash/market-frontend-shared-core/src/models/user';
import { getReferralLink } from '@jetslash/market-frontend-shared-core/src/api/referralLinksApi';

import Reporty from '@/utility/errorHandling/Reporty';
import authModalTitleFromSignUpState from '@/utility/signUp/authModalTitleFromSignUpState';

import { goToUrl } from '@/utility/urlHelpers';

import SignUpWizard from '@/components/authorization/SignUpWizard';
import Radio from '@/components/forms/fields/Radio';
import Card from '@/components/design/Card';
import { Row, Col, Page } from '@/components/layout';
import Icon from '@/components/Icon';
import DisplayFlashMessages from '@/components/layout/DisplayFlashMessages';
import DesignText from '@/components/typography/DesignText';
import ComponentLink from '@/components/navigation/ComponentLink';
import Header from '@/components/typography/Header';
import PageSeo from '@/components/seo/PageSeo';
import useCurrentUser from '@jetslash/market-frontend-shared-core/src/hooks/useCurrentUser';
import useSignUpWizard from '@/hooks/useSignUpWizard';
import useFlashMessage from '@/hooks/useFlashMessage';
import useQueryParams from '@/utility/navigation/useQueryParams';

const SignUpPage = () => {
  const { loading, signUpUser } = useSignUpWizard();
  const { currentUserIsAuthenticated, currentUserIsHydrated, currentUser } = useCurrentUser();
  // This prevents the need to block certain SignUp UX that rely on user sign up state
  const effectiveSignUpUser = signUpUser || currentUser;

  const hideFormToggle = effectiveSignUpUser && effectiveSignUpUser?.signUpState !== SIGN_UP_STATES.newMarketUser;
  const title = authModalTitleFromSignUpState(effectiveSignUpUser?.signUpState, 'Sign up');

  useEffect(() => {
    if (currentUserIsAuthenticated) {
      setTimeout(() => {
        // Ensures we redirect out of this page if a background authorization triggered a change in sign up state, without interferring with redirect logic
        goToUrl('/');
      }, 1000);
    }
  }, [currentUserIsAuthenticated]);

  const { addFlashMessage } = useFlashMessage();
  const { queryParams } = useQueryParams();
  const from = queryParams?.from;
  useEffect(() => {
    if (from) {
      let redirectMsg;
      if (from === 'delegate_request') {
        redirectMsg = 'Log in or sign up to respond to a request for delegate access.';
      } else if (from === 'delegate_invite') {
        redirectMsg = 'Log in or sign up to respond to an invite for delegate access.';
      } else if (queryParams?.utm_source === 'referral' && !!queryParams?.utm_content?.length) {
        getReferralLink(queryParams.utm_content, queryParams.utm_medium)
          .then((referralUtmPayload) => {
            addFlashMessage('success', referralUtmPayload.data.rewardDescriptionText);
          })
          .catch((e) => {
            Reporty.error(e);
          });
      }
      if (redirectMsg) {
        addFlashMessage('warning', redirectMsg);
      }
    }
  }, [from]);

  return (
    <Page>
      <PageSeo title="Sign Up" />
      {currentUserIsHydrated && (
        <Row style={{ marginTop: 60 }}>
          <Col
            xs={{ span: 24, offset: 0 }}
            sm={{ span: 22, offset: 1 }}
            md={{ span: 20, offset: 2 }}
            lg={{ span: 12, offset: 6 }}
          >
            <Card
              title={
                <div style={{ textAlign: 'center' }}>
                  <span>
                    <Icon name="user" />
                  </span>
                  <DesignText size="title">&nbsp;&nbsp;&nbsp;{title}</DesignText>
                </div>
              }
              loading={
                loading ||
                (effectiveSignUpUser?.signUpState &&
                  effectiveSignUpUser?.signUpState === SIGN_UP_STATES.accountFullyConfirmed)
              }
            >
              <Row>
                <Col span={24}>
                  <div
                    style={{
                      marginLeft: 10,
                      marginRight: 10,
                      marginBottom: 10,
                    }}
                  >
                    <DisplayFlashMessages />
                  </div>
                </Col>
              </Row>
              <Row style={hideFormToggle ? { display: 'none' } : {}}>
                <Col span={24}>
                  <div
                    style={{
                      marginLeft: 10,
                      marginRight: 10,
                      marginBottom: 10,
                    }}
                  >
                    <Radio.Group style={{ width: '100%' }} value="sign_up_selected">
                      <ComponentLink href="/users/log-in" shallow>
                        <Radio.Button
                          style={{ width: '50%', textAlign: 'center' }}
                          value="log_in_selected"
                          onClick={() => {}}
                        >
                          <DesignText strong color="primary">
                            Log In
                          </DesignText>
                        </Radio.Button>
                      </ComponentLink>
                      <Radio.Button style={{ width: '50%', textAlign: 'center' }} value="sign_up_selected">
                        <Header
                          level={1}
                          style={{
                            fontSize: 14,
                            display: 'inline',
                            lineHeight: 1,
                          }}
                          strong
                          color="primary"
                        >
                          Sign Up
                        </Header>
                      </Radio.Button>
                    </Radio.Group>
                  </div>
                </Col>
              </Row>
              <SignUpWizard />
            </Card>
          </Col>
        </Row>
      )}
    </Page>
  );
};

export default SignUpPage;
